/* Driver time-correction request form + the driver's own request history (read-only status).
   Drivers may propose a corrected start/end time with a reason; they can NEVER overwrite an
   approved record — a manager reviews every request. No green styling (muted gold theme). */
import { useCallback, useEffect, useState } from "react";
import { Timer, Loader2, Send } from "lucide-react";
import { api } from "@/lib/auth";

const STATUS_LABEL = {
  pending: "Pending review", approved: "Approved", rejected: "Rejected",
  revision_requested: "Revision requested",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function DriverCorrections({ trip }) {
  const [items, setItems] = useState(null);
  const [field, setField] = useState("end_at");
  const [proposed, setProposed] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    api.get("/driver/corrections").then((r) => setItems(r.data.corrections)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!trip || !proposed || reason.trim().length < 3) { setMsg("Pick a trip, a proposed time and a reason (3+ chars)."); return; }
    setBusy(true); setMsg(null);
    try {
      await api.post("/driver/corrections", {
        reservation_id: trip.reservation_id, field,
        proposed: new Date(proposed).toISOString(), reason: reason.trim(),
      });
      setProposed(""); setReason(""); setMsg("Submitted for manager review."); load();
    } catch { setMsg("Could not submit. Please try again."); } finally { setBusy(false); }
  };

  return (
    <div data-testid="driver-corrections" className="space-y-4">
      <div className="rounded-2xl border border-gold/20 bg-obsidian-800 p-4">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-gold-dark"><Timer className="h-3.5 w-3.5" /> Request a time correction</p>
        {trip ? (
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-muted-steel">
            <div className="rounded-lg border border-line bg-obsidian-900 p-2"><p className="uppercase text-dim-charcoal">Recorded start</p><p className="mt-0.5 text-ivory">{fmt(trip.clock?.start_at)}</p></div>
            <div className="rounded-lg border border-line bg-obsidian-900 p-2"><p className="uppercase text-dim-charcoal">Recorded end</p><p className="mt-0.5 text-ivory">{fmt(trip.clock?.end_at)}</p></div>
          </div>
        ) : <p className="mt-2 text-xs text-muted-steel">Select a trip first.</p>}
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            {[["start_at", "Start time"], ["end_at", "End time"]].map(([k, l]) => (
              <button key={k} data-testid={`correction-field-${k}`} onClick={() => setField(k)} className={`flex-1 rounded-lg border px-3 py-2 text-xs ${field === k ? "border-gold/50 bg-gold/10 text-gold-light" : "border-line text-muted-steel"}`}>{l}</button>
            ))}
          </div>
          <input data-testid="correction-proposed" type="datetime-local" value={proposed} onChange={(e) => setProposed(e.target.value)} className="w-full rounded-lg border border-line bg-obsidian-900 px-3 py-2 text-sm text-ivory" />
          <textarea data-testid="correction-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason for the correction" className="w-full rounded-lg border border-line bg-obsidian-900 px-3 py-2 text-sm text-ivory placeholder:text-dim-charcoal" />
          <button data-testid="correction-submit" disabled={busy} onClick={submit} className="flex w-full items-center justify-center gap-2 rounded-xl border border-gold/40 bg-gold/10 py-2.5 text-sm font-semibold text-gold-light hover:bg-gold/20 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit request</button>
          {msg && <p data-testid="correction-msg" className="text-[11px] text-gold-light">{msg}</p>}
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-gold-dark">My requests</p>
        {items === null ? <div className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-gold-light" /></div>
          : items.length === 0 ? <p className="py-6 text-center text-xs text-muted-steel">No correction requests yet.</p>
            : <div className="space-y-2">{items.map((c) => (
              <div key={c.correction_id} data-testid={`correction-item-${c.correction_id}`} className="rounded-xl border border-line bg-obsidian-800 p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-gold-light">#{c.confirmation_number || c.reservation_id?.slice(-6)}</span>
                  <span className="rounded-full border border-gold/30 bg-gold/10 px-2 py-0.5 text-[10px] text-gold-light">{STATUS_LABEL[c.status] || c.status}</span>
                </div>
                <p className="mt-1.5 text-muted-steel">{c.field === "start_at" ? "Start" : "End"}: <span className="text-ivory">{fmt(c.original)}</span> → <span className="text-gold-light">{fmt(c.proposed)}</span></p>
                <p className="mt-1 text-dim-charcoal">{c.reason}</p>
                {c.decision_note && <p className="mt-1 text-amber-300">Manager: {c.decision_note}</p>}
              </div>
            ))}</div>}
      </div>
    </div>
  );
}
